import { supabase } from './supabase';

export interface RumbleUserData {
  now: number;
  type: string;
  user_id: string;
  username: string;
  channel_id: string | null;
  channel_name: string | null;
  followers: {
    num_followers: number;
    num_followers_total: number;
  };
  subscribers: {
    num_subscribers: number;
  };
  livestreams: any[];
}

export interface RumbleSettings {
  id?: string;
  user_id: string;
  api_key: string;
  rumble_user_id: string;
  rumble_username: string;
  created_at?: string;
  updated_at?: string;
}

const RUMBLE_API_BASE = 'https://rumble.com/-livestream-api/get-data?key=';

/**
 * Extract API key from Rumble URL
 */
function extractApiKey(url: string): string | null {
  // Check if URL starts with the expected base
  if (!url.startsWith(RUMBLE_API_BASE)) {
    return null;
  }
  
  // Extract the key part
  const key = url.substring(RUMBLE_API_BASE.length);
  
  // Basic validation - should have some content
  if (!key || key.length < 10) {
    return null;
  }
  
  return key;
}

/**
 * Validate Rumble API key using Supabase Edge Function
 */
export async function validateRumbleApiKey(url: string): Promise<RumbleUserData> {
  const apiKey = extractApiKey(url);
  
  if (!apiKey) {
    throw new Error('Invalid Rumble API URL format. Please use the full URL from your Rumble dashboard.');
  }
  
  try {
    // Get current session for authentication
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      throw new Error('You must be signed in to validate API keys');
    }

    // Call Supabase Edge Function
    const { data, error } = await supabase.functions.invoke('validate-rumble-api', {
      body: { apiUrl: url },
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    });
    
    if (error) {
      console.error('Supabase function error:', error);
      throw new Error(error.message || 'Failed to validate API key');
    }
    
    if (!data) {
      throw new Error('No response from validation service');
    }

    // Handle error responses from the function
    if (data.error) {
      throw new Error(data.error);
    }
    
    // Validate the response has required fields
    if (!data.user_id || !data.username) {
      throw new Error('Invalid response from Rumble API. Please check your API key.');
    }
    
    return data as RumbleUserData;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Network error. Please check your connection and try again.');
  }
}

/**
 * Save Rumble API settings to Supabase (encrypted, user-specific)
 */
export async function saveRumbleSettings(apiUrl: string, rumbleData: RumbleUserData): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) {
    throw new Error('You must be logged in to save settings');
  }
  
  const apiKey = extractApiKey(apiUrl);
  if (!apiKey) {
    throw new Error('Invalid API URL');
  }
  
  const settings: Omit<RumbleSettings, 'id' | 'created_at' | 'updated_at'> = {
    user_id: user.id,
    api_key: apiKey,
    rumble_user_id: rumbleData.user_id,
    rumble_username: rumbleData.username,
  };
  
  // Upsert the settings (insert or update if exists)
  const { error } = await supabase
    .from('user_rumble_settings')
    .upsert(settings, {
      onConflict: 'user_id'
    });
  
  if (error) {
    console.error('Supabase error:', error);
    throw new Error('Failed to save settings. Please try again.');
  }
}

/**
 * Load Rumble settings for the current user
 */
export async function loadRumbleSettings(): Promise<RumbleSettings | null> {
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) {
    return null;
  }
  
  const { data, error } = await supabase
    .from('user_rumble_settings')
    .select('*')
    .eq('user_id', user.id)
    .single();
  
  if (error) {
    if (error.code === 'PGRST116') {
      // No settings found
      return null;
    }
    console.error('Error loading settings:', error);
    return null;
  }
  
  return data;
}

/**
 * Unlink Rumble account (clears Rumble-specific fields while preserving settings)
 *
 * This UPDATE (not DELETE) preserves:
 * - settings_id (id) - keeps TTS routing working
 * - user_id - keeps user ownership
 * - extension_settings - preserves personality, voice, etc.
 * - bot_enabled, tts_enabled - preserves toggle states
 * - custom_commands, timed_messages - preserves all configurations
 */
export async function deleteRumbleSettings(): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();

  if (!user) {
    throw new Error('You must be logged in to unlink Rumble');
  }

  // UPDATE instead of DELETE to preserve settings_id and extension settings
  const { error } = await supabase
    .from('user_rumble_settings')
    .update({
      api_key: null,
      rumble_user_id: null,
      rumble_username: null
    })
    .eq('user_id', user.id);

  if (error) {
    throw new Error('Failed to unlink Rumble account');
  }
}

/**
 * Check if user has valid Rumble connection
 */
export async function checkRumbleConnection(): Promise<boolean> {
  const settings = await loadRumbleSettings();
  return !!settings?.api_key;
}